import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";


const STAGE_LABELS = {
  order_placed:      "Order Placed",
  payment_confirmed: "Payment Confirmed",
  packaged:          "Packaged",
  shipped:           "Shipped",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered"
};

const STAGE_COLORS = {
  order_placed:      { bg: "#EEF2FF", color: "#4361C2" },
  payment_confirmed: { bg: "#EEF2FF", color: "#4361C2" },
  packaged:          { bg: "#FFF7E6", color: "#B45309" },
  shipped:           { bg: "#E6F0FF", color: "#1D4ED8" },
  out_for_delivery:  { bg: "#FFF0F0", color: "#DC2626" },
  delivered:         { bg: "#EDFDF4", color: "#15803D" }
};

export default function OrdersScreen() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchOrders();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

const fetchOrders = async () => {
  try {
    const res = await fetch(
      `https://eleven11-i6v4.onrender.com/api/orders?userId=${user?.id}&email=${encodeURIComponent(user?.email)}`
    );
      const data = await res.json();
      console.log("Orders response:", data);
      setOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (err) {
      console.error("Failed to load orders:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div style={styles.page}>

        <div style={styles.heroGlow} />
        <main style={styles.container}>
          <section style={styles.header}>
            <div>
              <p style={styles.kicker}>ELEVEN ACCOUNT</p>
              <h1 style={styles.title}>Your Orders</h1>
            </div>
            <button style={styles.backButton} onClick={() => navigate("/account")}>← Back</button>
          </section>
          <div style={styles.emptyCard}>
            <p style={styles.emptyText}>Loading orders...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
 
      <div style={styles.heroGlow} />

      <main style={styles.container}>

        <section style={styles.header}>
          <div>
            <p style={styles.kicker}>ELEVEN ACCOUNT</p>
            <h1 style={styles.title}>Your Orders</h1>
          </div>
          <button style={styles.backButton} onClick={() => navigate("/account")}>
            ← Back
          </button>
        </section>

        {orders.length === 0 && (
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}>📦</div>
            <p style={styles.emptyTitle}>No orders yet</p>
            <p style={styles.emptyText}>Once you make a purchase it will appear here.</p>
            <button style={styles.shopButton} onClick={() => navigate("/")}>
              Browse Parts
            </button>
          </div>
        )}

        {orders.length > 0 && (
          <div style={styles.list}>
            {orders.map((order) => {
              const stage = order.fulfillmentStatus || "order_placed";
              const badge = STAGE_COLORS[stage] || STAGE_COLORS.order_placed;
              const label = STAGE_LABELS[stage] || stage;

              const date = new Date(order.createdAt).toLocaleDateString("en-US", {
                month: "long", day: "numeric", year: "numeric"
              });

              const firstItem = order.items?.[0];
              const extraCount = (order.items?.length || 1) - 1;

              return (
                <div
                  key={order._id}
                  style={styles.orderCard}
                  onClick={() => navigate(`/account/orders/${order._id}`)}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 32px 72px rgba(24,35,60,0.14)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 20px 50px rgba(24,35,60,0.09)";
                  }}
                >
                  <div style={styles.cardTop}>
                    <div>
                      <p style={styles.orderNumber}>{order.orderNumber}</p>
                      <p style={styles.orderDate}>{date}</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ ...styles.badge, background: badge.bg, color: badge.color }}>
                        {label}
                      </span>
                      <span style={styles.chevron}>›</span>
                    </div>
                  </div>

                  <div style={styles.divider} />

                  <div style={styles.itemPreview}>
                    {firstItem?.image ? (
                      <img
                        src={`${firstItem.image}`}
                        alt={firstItem.title}
                        style={styles.itemImage}
                      />
                    ) : (
                      <div style={styles.itemImagePlaceholder}>📦</div>
                    )}
                    <div style={styles.itemInfo}>
                      <p style={styles.itemTitle}>{firstItem?.title || "OEM Part"}</p>
                      {extraCount > 0 && (
                        <p style={styles.itemExtra}>+{extraCount} more item{extraCount > 1 ? "s" : ""}</p>
                      )}
                    </div>
                    <div style={styles.orderTotal}>
                      <p style={styles.totalLabel}>Total</p>
                      <p style={styles.totalValue}>${Number(order.total || 0).toFixed(2)}</p>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}

      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "radial-gradient(circle at top, rgba(255,255,255,0.55), transparent 28%), linear-gradient(180deg, #f5f7fb 0%, #eef1f7 45%, #f7f8fb 100%)",
    fontFamily: "-apple-system, BlinkMacSystemFont, Inter, Helvetica, Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingTop: 90,
    paddingBottom: 80
  },
  heroGlow: {
    position: "absolute",
    top: -180,
    left: "50%",
    transform: "translateX(-50%)",
    width: 720,
    height: 420,
    background: "radial-gradient(circle, rgba(255,255,255,0.9), rgba(210,220,240,0.35), transparent 70%)",
    filter: "blur(10px)",
    pointerEvents: "none"
  },
  container: {
    width: "min(860px, calc(100% - 40px))",
    margin: "0 auto",
    position: "relative",
    zIndex: 2
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 32
  },
  kicker: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 2.4,
    color: "#7b7f89"
  },
  title: {
    margin: "6px 0 0",
    fontSize: 48,
    letterSpacing: -1.5,
    fontWeight: 800,
    color: "#050505"
  },
  backButton: {
    border: "1px solid rgba(0,0,0,0.08)",
    background: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(18px)",
    borderRadius: 999,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(20,30,50,0.08)"
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  orderCard: {
    borderRadius: 28,
    background: "rgba(255,255,255,0.82)",
    backdropFilter: "blur(24px)",
    border: "1px solid rgba(255,255,255,0.85)",
    boxShadow: "0 20px 50px rgba(24,35,60,0.09)",
    padding: "26px 30px",
    cursor: "pointer",
    transition: "transform 0.18s ease, box-shadow 0.18s ease"
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  orderNumber: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: "#050505",
    letterSpacing: -0.3
  },
  orderDate: {
    margin: "4px 0 0",
    fontSize: 14,
    color: "#8a8e98"
  },
  badge: {
    padding: "6px 14px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700
  },
  chevron: {
    fontSize: 28,
    color: "#b0b4bd",
    lineHeight: 1
  },
  divider: {
    height: 1,
    background: "rgba(0,0,0,0.06)",
    margin: "18px 0"
  },
  itemPreview: {
    display: "flex",
    alignItems: "center",
    gap: 16
  },
  itemImage: {
    width: 56,
    height: 56,
    borderRadius: 12,
    objectFit: "cover",
    background: "#f5f7fb",
    boxShadow: "0 4px 10px rgba(0,0,0,0.07)"
  },
  itemImagePlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 12,
    background: "#f0f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24
  },
  itemInfo: { flex: 1 },
  itemTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: "#1D1D1F",
    lineHeight: "20px"
  },
  itemExtra: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#8a8e98"
  },
  orderTotal: { textAlign: "right" },
  totalLabel: {
    margin: 0,
    fontSize: 12,
    color: "#8a8e98",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1
  },
  totalValue: {
    margin: "4px 0 0",
    fontSize: 22,
    fontWeight: 800,
    color: "#050505",
    letterSpacing: -0.5
  },
  emptyCard: {
    borderRadius: 28,
    background: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(255,255,255,0.85)",
    boxShadow: "0 20px 50px rgba(24,35,60,0.09)",
    padding: 60,
    textAlign: "center"
  },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyTitle: {
    margin: "0 0 8px",
    fontSize: 22,
    fontWeight: 800,
    color: "#050505"
  },
  emptyText: { margin: 0, fontSize: 16, color: "#8a8e98" },
  shopButton: {
    marginTop: 24,
    border: "none",
    background: "#050505",
    color: "#fff",
    borderRadius: 999,
    padding: "14px 28px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer"
  }
};